import "webrtc-adapter";

import DYLongLinkClient from "@/js/web/socket/websocket";
import { LoginMediaServer } from "@/js/web/socket/protocol";
import BusinessManager from "@js/controls";
import { Root } from "protobufjs";
import pb from "@pb/index.json";
import eventsCenter, { WEBRTCMSGTYPE, CHANNELMSGTYPE } from "@js/events";
import * as Accounts from "@js/account";
import * as Config from "@/config";
import { strReplaceLast, isGameHtml } from "@js/utils";
import { decode } from "@js/web/pbUtils";
import { base64ToArrayBuffer } from "@js/web/socket/unitPacket";
import stastic from "@js/stastic";
import ERROR from "@js/error";
import Logger from "@js/logger";
import { INamespace } from "protobufjs";

const WEBRTCTYPE = {
  candidate: "candidate",
  offer: "offer",
  answer: "answer",
  login: "login",
};

const root = Root.fromJSON(pb as INamespace);

class DYWebRtcClient {
  // static instance = new DYWebRtcClient();

  private static instance: DYWebRtcClient;
  private localPeerConnection: RTCPeerConnection | any;
  private localChannel: RTCDataChannel | null;
  private localChannelReady: boolean;
  private config: any;
  // 检测延迟的timer
  private delayTimer: any;
  private checkTimer: any;
  private lastReceiveMessageTime: number | undefined;
  private startConnectTime: any;
  private hasReportConnectTime: any;

  static getInstance() {
    // return DYWebRtcClient.instance;
    if (!this.instance) {
      this.instance = new DYWebRtcClient();
    }
    return this.instance;
  }

  constructor() {
    this.localPeerConnection = null;
    this.localChannel = null;
    this.localChannelReady = false;
    this.delayTimer = null;
    this.checkTimer = null;
    DYLongLinkClient.getInstance();

    this.setupSignalEvents();
  }

  public setup(config: any) {
    this.config = config;
    this.createRCTConnectionIfNeeded();
  }

  public isSupport() {
    let isSupport = false;
    [
      "RTCPeerConnection",
      "webkitRTCPeerConnection",
      "mozRTCPeerConnection",
      "RTCIceGatherer",
    ].forEach((item) => {
      if (item in window) {
        isSupport = true;
      }
    });
    return isSupport;
  }

  addEventListener(command: string, callback: any) {
    eventsCenter.on(command, callback);
  }

  async connect() {
    try {
      this.startConnectTime = new Date().getTime();
      BusinessManager.ChangeWebRtcInfo(
        WEBRTCTYPE.login,
        Accounts.getAccountId(),
        Accounts.getGameUid(),
        LoginMediaServer(1)
      );

      const offer = await this.localPeerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      });
      offer.sdp = this.modifyAudioSDP(offer.sdp);
      Logger.log(`local offer ${offer.sdp}`);
      await this.localPeerConnection.setLocalDescription(offer);
      BusinessManager.ChangeWebRtcInfo(
        WEBRTCTYPE.offer,
        Accounts.getAccountId(),
        Accounts.getGameUid(),
        offer
      );
    } catch (err: any) {
      Logger.log(`create offer err ${JSON.stringify(err?.message)}`);
      eventsCenter.emit(WEBRTCMSGTYPE.Error, {
        code: ERROR.CONST.switchGameError,
      });
      stastic.addTrackEvent("游戏错误", "webrtc", "发送icp失败");
    }
  }

  modifyAudioSDP(sdp: string) {
    if (sdp.indexOf("a=fmtp:111 minptime=10;useinbandfec=1")) {
      sdp = sdp.replace(
        "a=fmtp:111 minptime=10;useinbandfec=1",
        "a=fmtp:111 minptime=10;useinbandfec=1;stereo=1"
      );
    }

    sdp = sdp.replace("UDP/DTLS/SCTP", "DTLS/SCTP");
    sdp = sdp.replace(/\sa=extmap-allow-mixed\s/, "");
    return sdp;
  }

  changeCandidatePort(candidate: string) {
    const candidateStr = "candidate:";
    const pos = candidate.indexOf(candidateStr) + candidateStr.length;
    const fields = candidate.substring(pos).split(" ");

    const net = fields[2];
    let port = fields[5];
    const type = fields[7];

    if (port && net === "udp" && type === "host") {
      return [
        port,
        parseInt(port) - 2,
        parseInt(port) + 2,
        parseInt(port) - 4,
        parseInt(port) + 4,
      ];
    }

    port = fields[11];
    if (port && net === "udp" && type === "srflx") {
      return [
        port,
        parseInt(port) - 2,
        parseInt(port) + 2,
        parseInt(port) - 4,
        parseInt(port) + 4,
      ];
    }

    return null;
  }

  modifyCandidate(candidate: string) {
    const ports = this.changeCandidatePort(candidate);
    if (ports && Array.isArray(ports)) {
      const candidates = ports.map((item) => {
        return strReplaceLast(candidate, `${ports[0]}`, `${item}`);
      });
      return candidates;
    }
    return candidate;
  }

  async reconnect() {
    try {
      const offer = await this.localPeerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
        iceRestart: true,
      });
      offer.sdp = this.modifyAudioSDP(offer.sdp);
      await this.localPeerConnection.setLocalDescription(offer);
      BusinessManager.ChangeWebRtcInfo(
        WEBRTCTYPE.offer,
        Accounts.getAccountId(),
        Accounts.getGameUid(),
        offer
      );
    } catch (err) {
      // console.log(err);
      eventsCenter.emit(WEBRTCMSGTYPE.Error, {
        code: ERROR.CONST.createOfferError,
      });
      stastic.addTrackEvent("游戏错误", "webrtc", "createOffer");
    }
  }

  disconnect() {
    if (this.localChannel) {
      try {
        this.localChannel.close();
      } catch (err) {
        console.log(err);
      }

      this.removeChannelEvents(this.localChannel);
      this.localChannel = null;
    }

    if (this.localPeerConnection) {
      this.removeEvents();
      try {
        this.localPeerConnection.close();
      } catch (err) {
        console.log(err);
      }

      this.localPeerConnection = null;
    }
  }

  sendChannelData(data: any) {
    try {
      if (this.localChannelReady) {
        this.localChannel && this.localChannel.send(data);
      } else {
        console.log("sendCHannel not ready");
      }
    } catch (err) {
      console.log("通道出错", err);
      eventsCenter.emit(WEBRTCMSGTYPE.Error, {
        code: ERROR.CONST.sendChannelDataError,
      });
      stastic.addTrackEvent("游戏错误", "webrtc", "通道出错");
    }
  }

  // 连接webRTC
  private createRTConnection() {
    console.log("createRTConnection");
    Logger.log("createRTConnection");
    const config = {
      iceServers: Config.webRtcIceServers,
    };

    this.localPeerConnection = new RTCPeerConnection(config);
    // 绑定webRTC连接的事件
    this.setupEvents();
    // 创建通道并绑定相关事件
    this.createDataChannel();
    // 30秒后检查webRTC是否正常
    this.checkUnusualEventsOccur();
  }

  public createDataChannel() {
    console.log("createDataChannel");

    this.localChannel = this.localPeerConnection.createDataChannel(
      "dywebrtcdata",
      { ordered: false }
    );
    this.setupChannelEvents(this.localChannel);
  }

  private createRCTConnectionIfNeeded() {
    if (!this.localPeerConnection) {
      this.createRTConnection();
    }
  }

  // 监听webRTC相关事件
  private setupEvents() {
    this.localPeerConnection.onicecandidate = this.onicecandidate.bind(this);
    this.localPeerConnection.ontrack = this.ontrack.bind(this);
    // this.localPeerConnection.onaddstream = this.onAddStream.bind(this);
    this.localPeerConnection.onconnectionstatechange =
      this.onconnectionstatechange.bind(this);
    this.localPeerConnection.ondatachannel = this.ondatachannel.bind(this);
  }

  private removeEvents() {
    this.localPeerConnection.onicecandidate = null;
    this.localPeerConnection.ontrack = null;
    this.localPeerConnection.onconnectionstatechange = null;
    this.localPeerConnection.ondatachannel = null;
  }

  private clearDelayTimer() {
    this.delayTimer && clearInterval(this.delayTimer);
  }

  // 监听通道事件
  private setupChannelEvents(channel: RTCDataChannel | null) {
    if (!channel) {
      return;
    }

    console.log("setupChannelEvents");

    // 通道开启
    channel.onopen = (event) => {
      if (this.localChannel === channel) {
        this.localChannelReady = true;
        // 通过通道发送本地时间到对端，计算网络延迟
        this.delayTimer = setInterval(() => {
          const curnow = new Date().getTime();
          this.sendChannelData(BusinessManager.WebrtcPing(curnow));

          // if (Math.abs(curnow - this.lastReceiveMessageTime) >= 5000) {
          //   eventsCenter.emit(WEBRTCMSGTYPE.Error, {
          //     code : ERROR.CONST.channelConnectError
          //   });

          //   Logger.log("datachanngel message currupt");
          // }
        }, 1000);
      }
      console.log("datachanngel onopen");
    };

    // 通道关闭，清除定时器
    channel.onclose = (event) => {
      this.localChannelReady = false;
      this.clearDelayTimer();
      console.log("datachanngel onclose");
    };

    // 通道错误，触发webRTC的错误事件总线
    channel.onerror = (event) => {
      this.localChannelReady = false;
      this.clearDelayTimer();
      eventsCenter.emit(WEBRTCMSGTYPE.Error, {
        code: ERROR.CONST.channelConnectError,
      });
      console.log("datachanngel onerror", event);
      Logger.log(
        `datachanngel onerror ${JSON.stringify((event as any)?.error?.message)}`
      );
    };

    // 通道发消息过来
    channel.onmessage = (event) => {
      // console.log('onmessage', event.data);

      this.lastReceiveMessageTime = new Date().getTime();

      let message = event.data;
      if (event.data) {
        const resBuffer = base64ToArrayBuffer(event.data);
        const rpcOut = decode(root, "RPCInput", resBuffer);
        // 网络相关数据处理
        if (rpcOut && rpcOut.func === "WebrtcPingRes") {
          const res = decode(root, "WebrtcPingRes", rpcOut.req);
          // 计算网络延迟
          const now = new Date().getTime();
          const value = now > res.msg.ping ? now - res.msg.ping : 0;
          // console.log('WebrtcPingRes', res.msg.ping, now);
          // Logger.log(`cpucount: ${res.cpucount} --- cpuusage: ${res.cpuusage} --- gpuusage: ${res.gpuusage} --- memusage: ${res.memusage} --- macid: ${res.macid}`);
          const cpuData =
            Config.env === "pre" || Config.env === "cputest"
              ? {
                  cpucount: res.cpucount,
                  cpuusage: res.cpuusage,
                  gpuusage: res.gpuusage,
                  memusage: res.memusage,
                  macid: res.macid,
                }
              : {};
          message = {
            type: CHANNELMSGTYPE.Moniter,
            data: {
              bitrate: res.bitrate,
              framerate: res.framerate,
              width: res.width,
              height: res.height,
              loss: res.loss,
              rtt: res.rtt,
              delay: value,
              ...cpuData,
            },
          };
        } else if (rpcOut && rpcOut.func === "WebrtcMouseShapeReq") {
          // 鼠标样式处理
          const res = decode(root, "WebrtcMouseShapeReq", rpcOut.req);
          console.log("WebrtcMouseShapeReq", res);
          message = {
            type: CHANNELMSGTYPE.MouseShape,
            data: res.msg,
          };
        } else if (rpcOut && rpcOut.func === "WebrtcMousePostionReq") {
          // 鼠标位置处理
          // const res = decode(root, 'WebrtcMousePostionReq', rpcOut.req);
          // console.log('WebrtcMousePostionReq', res);
          // message = {
          //   type: CHANNELMSGTYPE.MousePostion,
          //   data: res.msg
          // };
        }
      }
      eventsCenter.emit(WEBRTCMSGTYPE.Message, message);
    };
  }

  private removeChannelEvents(channel: RTCDataChannel) {
    channel.onopen = null;
    channel.onclose = null;
    channel.onerror = null;
    channel.onmessage = null;
  }

  // 判断媒体流是否正常
  private hasStream(streams: any) {
    return (
      streams &&
      streams.length > 0 &&
      typeof streams[0] !== "undefined" &&
      !streams[0].ended
    );
  }

  // 有数据通道
  private ondatachannel(event: any) {
    event.channel && this.setupChannelEvents(event.channel);
  }

  // webRTC有新的主机时，通知webSocket信令服务器
  private onicecandidate(event: any) {
    if (event.candidate) {
      console.log("webrtc onicecandidate:", event.candidate.candidate);
      // 格式化主机
      const candidate = this.modifyCandidate(event.candidate.candidate);
      console.log("new onicecandidate:", candidate);
      // 有多个主机
      if (candidate && Array.isArray(candidate)) {
        candidate.forEach((item) => {
          const newCandidate = { ...event.candidate, candidate: item };
          // BusinessManager.ChangeWebRtcInfo(WEBRTCTYPE.candidate, Accounts.getAccountId(), Accounts.getGameUid(), newCandidate);
          Logger.log(`webrtc new onicecandidate: ${newCandidate}`);
        });
      } else {
        // 只有一个主机
        Logger.log(`webrtc new onicecandidate: ${event.candidate}`);
        // BusinessManager.ChangeWebRtcInfo(WEBRTCTYPE.candidate, Accounts.getAccountId(), Accounts.getGameUid(), event.candidate);
      }
    }
  }

  // 处理云主机主动发的SDP
  private async answer(desc: any) {
    // 保证连接正常
    this.createRCTConnectionIfNeeded();
    try {
      // 设置远程描述
      await this.localPeerConnection.setRemoteDescription(desc);
      // 本地创建新的SDP再传给云主机
      const answer = await this.localPeerConnection.createAnswer();
      answer.sdp = this.modifyAudioSDP(answer.sdp);
      Logger.log(`webrtc onicecandidate: ${answer.sdp}`);
      await this.localPeerConnection.setLocalDescription(answer);

      // 通过信令服务器发过去
      BusinessManager.ChangeWebRtcInfo(
        WEBRTCTYPE.answer,
        Accounts.getAccountId(),
        Accounts.getGameUid(),
        answer.toJSON()
      );
    } catch (err) {
      // console.error(err);
      eventsCenter.emit(WEBRTCMSGTYPE.Error, {
        code: ERROR.CONST.answerError,
      });
      stastic.addTrackEvent("游戏错误", "webrtc", "answer");
    }
  }

  // 第一次接入媒体流时，通知信令服务器
  private ontrack(event: any) {
    console.log("webrtc onaddstream: ", event);
    if (event.track.kind == "video" && this.hasStream(event.streams)) {
      eventsCenter.emit(WEBRTCMSGTYPE.Stream, event.streams[0]);
    }
  }

  // 有新的媒体流时，开始渲染
  private onAddStream(event: any) {
    Logger.log(`webrtc onAddStream stream added `);
    eventsCenter.emit(WEBRTCMSGTYPE.Stream, event.stream);
    eventsCenter.emit(WEBRTCMSGTYPE.Render);
    this.reportRenderEvent();
  }

  private reportRenderEvent() {
    if (this.hasReportConnectTime) {
      return;
    }

    if (this.startConnectTime) {
      stastic.addTrackEvent(
        "进入游戏",
        "render",
        "success",
        new Date().getTime() - this.startConnectTime
      );
      this.hasReportConnectTime = true;
    }
  }

  // 监听连接状态的改变
  private onconnectionstatechange(event: any) {
    switch (this.localPeerConnection.connectionState) {
      // 连接成功，开始渲染
      case "connected":
        Logger.log("The connection has become fully connected");
        eventsCenter.emit(WEBRTCMSGTYPE.Render);
        this.reportRenderEvent();
        break;
      // 连接断开
      case "disconnected":
        Logger.log("The connection disconnected");
        break;
      // 连接失败
      case "failed":
        Logger.log(
          "One or more transports failed has terminated unexpectedly or in an error"
        );
        // eventsCenter.emit(WEBRTCMSGTYPE.Error, { code : ERROR.CONST.webrtcFailed});
        stastic.addTrackEvent("游戏错误", "webrtc", "connection failed");
        break;
      // 连接关闭
      case "closed":
        Logger.log("The connection has been closed");
        break;
      default:
        break;
    }
  }

  // 30秒后检查webRTC是否正常
  private checkUnusualEventsOccur() {
    if (!this.checkTimer) {
      this.checkTimer = setTimeout(() => {
        const receiver = this.localPeerConnection
          ? this.localPeerConnection.getReceivers()
          : null;
        console.log("checkUnusualEventsOccur", receiver);
        let webrtcSuccess = false;
        if (receiver) {
          receiver.forEach((item: any) => {
            item.getStats().then((res: any) => {
              console.log("item getstat:", res.size);
              if (res.size > 0) {
                webrtcSuccess = true;
                Logger.log("webrtcSuccess size success");
              } else {
                Logger.log("webrtcSuccess size failed");
              }

              if (!webrtcSuccess) {
                eventsCenter.emit(WEBRTCMSGTYPE.Error, {
                  err: "checkUnusualEventsOccur timeout",
                  code: ERROR.CONST.webrtcTimeout,
                });
                stastic.addTrackEvent("游戏错误", "webrtc", "conntimeout");
                Logger.log("webrtc conntimeout");
              } else {
                eventsCenter.emit(WEBRTCMSGTYPE.Render);
                this.reportRenderEvent();
              }
            });
          });
        } else {
          eventsCenter.emit(WEBRTCMSGTYPE.Error, {
            err: "checkUnusualEventsOccur timeout",
            code: ERROR.CONST.webrtcTimeout,
          });

          Logger.log("webrtc no receiver");
        }

        this.checkTimer && clearTimeout(this.checkTimer);
      }, 25000);
    }
  }

  public openLocalUserMedia() {
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then((res) => {
        this.localPeerConnection.addStream(res);
      })
      .catch((err) => {
        console.log("openLocalUserMedia error:", err);
      });
  }

  private createICECandidate(sdp: string, ip: string) {
    const sdpConfig = JSON.parse(sdp);

    const splitArr: Array<string> = sdpConfig.candidate.split(" ");
    if (splitArr.length > 8) {
      if (splitArr[2] === "udp" && splitArr[7] === "srflx" && ip) {
        const tempip = splitArr[4];
        splitArr[4] = ip;
        sdpConfig.candidate = splitArr.join(" ");
        Logger.log(
          `[createICECandidate] ip ${tempip} ${ip} ${sdp} ${sdpConfig.candidate}`
        );
      }
    }

    return new RTCIceCandidate(sdpConfig);
  }

  // 处理webRTC信令
  private async handleWebRtcNotify(pushMsg: any) {
    // 非游戏页面退出
    if (!isGameHtml()) {
      return;
    }

    const msg = pushMsg.obj.msg;
    // this.toId = msg.fromUid;
    // 保证连接初始化了
    this.createRCTConnectionIfNeeded();

    try {
      if (msg.type == WEBRTCTYPE.candidate) {
        // 有可用主机
        Logger.log(`[webrtcNotify] candidate ${msg.content}`);
        // 本地添加主机进去
        // TODO 多个ip
        const gameNode: any = Accounts.getCurrentGameNode();
        const promiseArr: any = [];
        if (gameNode) {
          if (Array.isArray(gameNode.addrs) && gameNode.addrs.length > 0) {
            for (const addr of gameNode.addrs) {
              promiseArr.push(
                this.localPeerConnection.addIceCandidate(
                  this.createICECandidate(msg.content, addr.ip)
                )
              );
            }
          } else if (gameNode.ip) {
            promiseArr.push(
              this.localPeerConnection.addIceCandidate(
                this.createICECandidate(msg.content, gameNode.ip)
              )
            );
          }
        }
        await Promise.all(promiseArr);
      } else if (msg.type == WEBRTCTYPE.answer) {
        // 云主机发送远端的应答SDP过来，本地保存起来
        Logger.log(`[webrtcNotify] answer ${msg.content}`);
        await this.localPeerConnection.setRemoteDescription(
          JSON.parse(msg.content)
        );
        Accounts.setRTCConnected(1);
      } else if (msg.type == WEBRTCTYPE.offer) {
        // 云主机主动发SDP过来
        Logger.log(`[webrtcNotify] offer ${msg.content}`);
        await this.answer(JSON.parse(msg.content));
      }
    } catch (err: any) {
      Logger.log(
        `handleWebRtcNotify err ${JSON.stringify(err?.message || err)}`
      );
      // eventsCenter.emit(WEBRTCMSGTYPE.Error, {
      //   code : ERROR.CONST.webrtcNotifyError
      // });
      stastic.addTrackEvent("游戏错误", "webrtc", "handleWebRtcNotify");
    }
  }

  // 监听服务器推送的webRTC信令
  private setupSignalEvents() {
    const socket = DYLongLinkClient.getInstance();
    socket.on(
      `${DYLongLinkClient.CLOUNDGAMECMD.SVR_WEBRTC_CHANGE_INFO_NOTIFY}`,
      this.handleWebRtcNotify.bind(this)
    );
  }
}

export default DYWebRtcClient;
